import React from 'react';
import {View, ViewStyle} from 'react-native';
import {get, noop} from 'lodash';

import Icon from '/component/base/Icon/Icon';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity/TouchableOpacity';
import {DownlineProfile} from '/constant/TestIDs';
import {
  ARRIVES,
  CANCELLED,
  DELIVERED,
  IN_PROCESS,
  RESCHEDULED,
  SHIPPED,
} from '/enum/clientJourneyTasks.enum';
import {useTranslation} from '/provider/TranslationProvider';
import {getDateUtilDefaultInstance} from '/util/date.util';
import testProperties from '/util/testing/testProperties';

import {styles} from './OrderWidget.style';
import {
  getFormattedShippingStatus,
  getOrderStatusIconName,
  onTrackingNumberPress,
  shouldRenderTrackingDetails,
} from './OrderWidget.util';

interface OrderPackage {
  status: string;
  trackingNumber?: string;
  trackingUrl?: string;
  scheduledTimestamp?: string;
}

interface OrderWidgetProps {
  title: string | null;
  orderStatus: string | null;
  isRightArrowIcon: boolean;
  onPress: () => void;
  orderPackages: OrderPackage[];
  orderShippingStatus: string | null;
  popoverShow: () => void;
  currentScreen: string;
  style: {basePadding?: ViewStyle};
}

export const OrderWidget: React.FC<OrderWidgetProps> = ({
  title = null,
  orderStatus,
  isRightArrowIcon = false,
  onPress = noop,
  orderPackages = [],
  orderShippingStatus = null,
  popoverShow = noop,
  currentScreen = '',
  style: styleProp = {},
}: OrderWidgetProps) => {
  const {ts, tt} = useTranslation(
    `component.partial.orderWidget.${orderStatus}`,
  );
  const renderOrderDescription = (description: any) => {
    return (
      <View style={styles.noOrderDescriptionContainer}>
        <Text
          style={{...styles.orderDateText, ...styles.noOrderDescription}}
          {...testProperties(DownlineProfile.cjFirstOrderNoOrdersLabel)}>
          {ts('description', description)}
        </Text>
      </View>
    );
  };

  const renderNoOrderStatus = () => {
    return (
      <Text
        style={styles.orderStatusText}
        {...testProperties(DownlineProfile.cjFirstOrderStatusLabel)}>
        {ts('status', 'Not yet placed')}
      </Text>
    );
  };

  const renderOrderStatus = (packageStatus: any) => {
    const status =
      packageStatus === ARRIVES || packageStatus === RESCHEDULED
        ? SHIPPED
        : packageStatus;

    return (
      <View style={styles.iconContainer}>
        <Icon
          // @ts-expect-error TS(2769): No overload matches this call.
          style={styles.orderStatusIcon}
          name={getOrderStatusIconName(status)}
          testID={DownlineProfile.cjFirstOrderStatusIcon}
        />
        <Text
          style={styles.orderStatusText}
          testID={DownlineProfile.cjFirstOrderStatusLabel}>
          {tt('firstOrderStatus', status)}
        </Text>
      </View>
    );
  };

  // @ts-expect-error TS(7030): Not all code paths return a value.
  const renderSinglePackageStatus = () => {
    if (orderPackages?.length === 1) {
      return renderOrderStatus(
        getFormattedShippingStatus(orderPackages[0]?.status),
      );
    } else {
      if (orderPackages?.length === 0 && orderShippingStatus) {
        return renderOrderStatus(
          getFormattedShippingStatus(orderShippingStatus),
        );
      }
    }
  };

  // @ts-expect-error TS(7030): Not all code paths return a value.
  const renderOrderArrivalDate = () => {
    const status = getFormattedShippingStatus(orderPackages[0]?.status);
    if (
      orderPackages?.length === 1 &&
      orderPackages[0]?.trackingNumber &&
      orderPackages[0]?.trackingUrl &&
      status !== DELIVERED &&
      status !== IN_PROCESS &&
      status !== CANCELLED
    ) {
      const packageDate = getDateUtilDefaultInstance().formatUTC(
        get(orderPackages, '[0].scheduledTimestamp', ''),
      );

      return (
        // @ts-expect-error TS(2322): Type '{ children: Element[]; testID: string; acces... Remove this comment to see the full error message
        <TouchableOpacity
          style={styles.orderDateContainer}
          {...testProperties(DownlineProfile.cjFirstOrderTo, true)}>
          <Text
            style={styles.orderDateText}
            {...testProperties(DownlineProfile.cjFirstOrderArrivalDateLabel)}>
            {ts('arrivalDate', 'Estimated arrival ')}
          </Text>
          <Text
            variant="body"
            style={styles.orderDateText}
            {...testProperties(DownlineProfile.cjFirstOrderArrivalDateValue)}>
            {getDateUtilDefaultInstance().formatDate(
              packageDate,
              'MMMM d, yyyy',
            )}
          </Text>
        </TouchableOpacity>
      );
    } else if (orderPackages?.length > 1) {
      return (
        // @ts-expect-error TS(2322): Type '{ children: Element; testID: string; accessi... Remove this comment to see the full error message
        <TouchableOpacity
          style={{
            ...styles.orderDateContainer,
            ...styles.orderMultipleDateContainer,
          }}
          {...testProperties(DownlineProfile.cjFirstOrderTo, true)}>
          <Text
            style={styles.orderDateText}
            {...testProperties(DownlineProfile.cjFirstOrderArrivalDateLabel)}>
            {ts('multipleArrivalDate', 'Multiple arrival dates ')}
          </Text>
        </TouchableOpacity>
      );
    }
  };

  const renderTrackingDetails = (
    trackingNumber: any,
    trackingUrl: any,
    packageStatus: any,
  ) => {
    if (trackingNumber && trackingUrl) {
      return (
        <View
          style={
            packageStatus === DELIVERED
              ? {
                  ...styles.orderTrackingContainer,
                  ...styles.orderTrackingContainerMargin,
                }
              : styles.orderTrackingContainer
          }>
          <Text
            style={styles.orderTrackingText}
            {...testProperties(
              DownlineProfile.cjFirstOrderTrackingNumberLabel,
            )}>
            {ts('trackingText', 'Tracking #')}
          </Text>
          <TouchableOpacity
            onPress={onTrackingNumberPress(
              {popoverShow},
              currentScreen,
              trackingUrl,
            )}
            {...testProperties(
              DownlineProfile.cjFirstOrderTrackingNumberButton,
              true,
            )}>
            <Text
              style={styles.orderTrackingIdText}
              {...testProperties(DownlineProfile.cjFirstOrderTrackingNumber)}>
              {trackingNumber}
            </Text>
          </TouchableOpacity>
        </View>
      );
    } else {
      return renderOrderDescription('Tracking details are not available');
    }
  };

  // @ts-expect-error TS(7030): Not all code paths return a value.
  const renderSinglePackageDetails = () => {
    if (
      orderPackages?.length === 1 &&
      shouldRenderTrackingDetails(
        getFormattedShippingStatus(orderPackages[0].status),
      )
    ) {
      return renderTrackingDetails(
        orderPackages[0]?.trackingNumber,
        orderPackages[0]?.trackingUrl,
        getFormattedShippingStatus(orderPackages[0]?.status),
      );
    }
  };
  // @ts-expect-error TS(7030): Not all code paths return a value.
  const renderMultiplePackageDetails = () => {
    const packageLength = orderPackages?.length;
    if (packageLength > 1) {
      return (
        <>
          <View style={styles.divider} />
          {orderPackages.map((pkg: any, i: any) => {
            const packageCount = i + 1;
            const packageStatus = getFormattedShippingStatus(pkg?.status);
            const orderMltiplePackageContainerStyle = {
              ...styles.widgetContainer,
              ...styles.multiplePackageContainer,
            };
            const multiPackageContainer =
              packageCount === orderPackages?.length
                ? orderMltiplePackageContainerStyle
                : {
                    ...orderMltiplePackageContainerStyle,
                    ...styles.multiPackageContainerMargin,
                  };
            return (
              <View key={i} style={multiPackageContainer}>
                <View
                  style={{
                    ...styles.widgetContainer,
                    ...styles.packageContainer,
                  }}>
                  {tt('PackageNumber', () => (
                    <Text
                      style={styles.packageText}
                      {...testProperties(
                        DownlineProfile.cjFirstOrderPackagesCount,
                      )}>
                      Package {packageCount} of {packageLength}
                    </Text>
                  ))}
                  <View style={styles.multiOrderContainer}>
                    {renderOrderStatus(packageStatus)}
                  </View>
                </View>
                <View style={styles.firstOrderContainer}>
                  {shouldRenderTrackingDetails(packageStatus) &&
                    // @ts-expect-error TS(2554): Expected 3 arguments, but got 2.
                    renderTrackingDetails(
                      pkg?.trackingNumber,
                      pkg?.trackingUrl,
                    )}
                </View>
              </View>
            );
          })}
        </>
      );
    }
  };

  return (
    <View
      style={{...styles.widgetBase, ...(styleProp?.basePadding || {})}}
      {...testProperties(DownlineProfile.cjFirstOrderView, true)}>
      <View style={styles.widgetContainer} {...testProperties(null, true)}>
        <View style={styles.widgetTitleContainer}>
          <Text
            style={styles.titleText}
            {...testProperties(DownlineProfile.cjFirstOrderTitleLabel)}>
            {tt('title', title)}
          </Text>
        </View>
        <View style={styleProp?.widgetStatusContainer}>
          {renderSinglePackageStatus()}
          {orderStatus === 'noOrder' && renderNoOrderStatus()}
          {isRightArrowIcon && (
            // @ts-expect-error TS(2322): Type '{ children: Element; testID: string; accessi... Remove this comment to see the full error message
            <TouchableOpacity
              onPress={onPress}
              {...testProperties(DownlineProfile.cjFirstOrderTo, true)}>
              <Icon name={'chevron-right'} style={styles.arrowIcon} />
            </TouchableOpacity>
          )}
        </View>
      </View>
      {renderOrderArrivalDate()}
      {renderSinglePackageDetails()}
      {renderMultiplePackageDetails()}
      {orderStatus !== 'noOrder' &&
        orderPackages?.length === 0 &&
        renderOrderDescription('Order details are not available')}
      {orderStatus === 'noOrder' &&
        renderOrderDescription(
          'Work with this Client to place their first order and start their health journey',
        )}
    </View>
  );
};
